package com.pincer.core.contracts

import com.pincer.core.PincerException
import com.pincer.core.InvalidData
import com.pincer.core.parsePincerException
import com.pincer.core.model.Device
import com.pincer.core.model.tree.Pincer
import com.pincer.core.model.patches.PincerPatch
import com.pincer.core.functions.extractIssuer
import kotlin.random.Random
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.SerializationException

@Serializable
data class PincerRequest(
    val type: String,
    val jwt: String? = null,
    val pid: String? = null,
    val sid: Long? = null,
    val patch: PincerPatch? = null,
    val credential: String? = null,
    val code: String? = null,
    val allDevices: Boolean? = null,
    val locale: String? = null,
    val zone: String? = null,
) {

    val id: String = Random.nextInt(100000000).toString()

    companion object {
        fun parse(json: String): PincerRequest {
            try { return Json.decodeFromString<PincerRequest>(serializer(), json) }
            catch (jde: SerializationException) { throw InvalidData("There's a problem with the PincerRequest json") }
        }
    }

    internal fun jwt(): String                   = jwt ?: throw InvalidData("JWT missing")
    internal fun pid(): String                   = pid ?: throw InvalidData("PID missing")
    internal fun sid(): Long                     = sid ?: throw InvalidData("SID missing")
    internal fun patch(): PincerPatch            = patch ?: throw InvalidData("Patch missing")
    internal fun credential(): String            = credential ?: throw InvalidData("Credential missing")
    internal fun code(): String                  = code ?: throw InvalidData("Code missing")
    internal fun allDevices(): Boolean           = allDevices ?: throw InvalidData("AllDevices missing")
    internal fun locale(): String                = locale ?: throw InvalidData("Locale missing")
    internal fun zone(): String                  = locale ?: throw InvalidData("Zone missing")

    internal fun deviceIdFromJwt(): String? {
        if (jwt == null) return null
        return extractIssuer(jwt)
    }

    fun json(): String {
        return Json.encodeToString(serializer(), this)
    }
}

@Serializable
data class PincerResponse(
    val req: String,
    val deviceId: String? = null,
    val string: String? = null,
    val long: Long? = null,
    val pincer: Pincer? = null,
    val pincerPatch: PincerPatch? = null,
    val device: Device? = null,
    val pincerException: PincerException? = null,
) {
    companion object {
        fun parse(json: String): PincerResponse {
            try {
                val response = Json.decodeFromString<PincerResponse>(serializer(), json)
                if (response.pincerException == null) return response
                // Extra bit needed here to get the right class of exception
                // See note in Exceptions.kt re. polymorphism/serialization
                return PincerResponse(req = response.req, pincerException = parsePincerException(response.pincerException.json()))
            }
            catch (jde: SerializationException) { throw InvalidData("There's a problem with the PincerResponse json") }
        }
    }
    fun json(): String {
        return Json.encodeToString(serializer(), this)
    }
}

interface RequestContract {
    suspend fun process(request: PincerRequest): PincerResponse
}

